import React, { useState } from "react";
import PropTypes from "prop-types";

/**
 * Admin section for viewing and editing credit costs per model family.
 * Inline editing: click the value, change it, click Save.
 */
const CreditPricingSection = ({ prices, onUpdate }) => {
  const [editingFamily, setEditingFamily] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  // [U2 FIX] Surface validation errors instead of silently returning
  const [editError, setEditError] = useState("");

  const startEdit = (price) => {
    setEditingFamily(price.modelFamily);
    setEditValue(String(price.creditsPerMessage));
    setEditError("");
  };

  const cancelEdit = () => {
    setEditingFamily(null);
    setEditValue("");
    setEditError("");
  };

  const saveEdit = async (family) => {
    const val = Number.parseInt(editValue, 10);
    // [U2 FIX] Show error instead of silent return
    if (Number.isNaN(val) || val < 1) {
      setEditError("Must be a positive integer (≥ 1)");
      return;
    }
    setEditError("");

    setSaving(true);
    try {
      await onUpdate(family, val);
      setEditingFamily(null);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e, family) => {
    if (e.key === "Enter") saveEdit(family);
    if (e.key === "Escape") cancelEdit();
  };

  return (
    <div className="admin-credits-section">
      <div className="admin-credits-section-header">
        <h3>Credit Pricing</h3>
      </div>
      {/* [U5 FALSE POSITIVE] Hint is accurate — adminCreditService.updateCreditPrice()
          calls reloadCreditPriceCache() synchronously before responding, so the
          in-memory cache used by the chat path is updated immediately. */}
      <p className="admin-form-hint admin-section-hint">
        How many credits each model costs per message. Changing this affects all
        future messages immediately.
      </p>

      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Model Family</th>
              <th>Credits per Message</th>
              <th>Last Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* [C4 FIX] Empty state consistent with CreditPackagesSection */}
            {prices.length === 0 ? (
              <tr>
                <td colSpan={4} className="admin-empty-row">
                  No credit prices configured yet.
                </td>
              </tr>
            ) : (
              /* [U4 FIX] Dim row while saving for visual feedback */
              prices.map((price) => (
                <tr
                  key={price.modelFamily}
                  className={
                    saving && editingFamily === price.modelFamily
                      ? "admin-row-saving"
                      : undefined
                  }
                >
                  <td>
                    <strong className="admin-family-name">
                      {price.modelFamily}
                    </strong>
                  </td>
                  <td>
                    {editingFamily === price.modelFamily ? (
                      <>
                        <input
                          type="number"
                          min="1"
                          max="10000"
                          className="admin-price-input"
                          value={editValue}
                          onChange={(e) => {
                            setEditValue(e.target.value);
                            setEditError("");
                          }}
                          onKeyDown={(e) => handleKeyDown(e, price.modelFamily)}
                          autoFocus
                        />
                        {/* [U2 FIX] Inline validation error */}
                        {editError && (
                          <div className="admin-field-error">{editError}</div>
                        )}
                      </>
                    ) : (
                      <span className="admin-limits-cell">
                        {price.creditsPerMessage}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="admin-form-hint">
                      {price.updatedAt
                        ? new Date(price.updatedAt).toLocaleDateString()
                        : "—"}
                    </span>
                  </td>
                  <td>
                    {editingFamily === price.modelFamily ? (
                      <span className="admin-inline-actions">
                        <button
                          className="admin-btn admin-btn-primary"
                          onClick={() => saveEdit(price.modelFamily)}
                          disabled={saving}
                        >
                          {saving ? "..." : "Save"}
                        </button>
                        <button
                          className="admin-btn admin-btn-cancel"
                          onClick={cancelEdit}
                          disabled={saving}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        className="admin-btn admin-btn-edit"
                        onClick={() => startEdit(price)}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

CreditPricingSection.propTypes = {
  prices: PropTypes.arrayOf(
    PropTypes.shape({
      modelFamily: PropTypes.string.isRequired,
      creditsPerMessage: PropTypes.number.isRequired,
      updatedAt: PropTypes.string,
    }),
  ).isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default CreditPricingSection;
